import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { OnVisible } from '@americanexpress/data-manager-utils';
import { Experiment, Variant } from '@americanexpress/one-experiments';
import isDeepEqual from '../../utils/isDeepEqual';
import CategoryList from './CategoryList';
import { locationShape } from '../../shape/locationShape';
import styles from './Suggestions.scss';
import locationContext from '../../context/locationsContext';
import { RECENT_SEARCHES_KEY } from '../../constants/categoryConstants';
import { getAnalyticsUtils } from '../../utils/getAnalyticsUtils';
import RecordRecentSearchesFeedback from './RecordRecentSearchesFeedback';

const SuggestionsList = ({ suggestions }) => {
  const {
    state: {
      locationsShown,
      isRoundTrip,
      inputWrapperRef,
    },
    actions: {
      updateShownLocations,
      updateHasRecentSearchDisplayed,
    },
    config: {
      lineOfBusiness: lob,
    },
  } = React.useContext(locationContext);

  const filteredSuggestions = Object.values(suggestions)
    .filter((suggestion) => suggestion.suggestions?.length > 0);

  const mergedSuggestions = filteredSuggestions
    .reduce((previousList, currentType) => [...previousList, ...currentType.suggestions], []);

  // todo: refactor this. Can't have setState on render body
  if (!isDeepEqual(mergedSuggestions, locationsShown)) {
    updateShownLocations(mergedSuggestions);
  }

  let indexOffset = 0;
  const getCategoryOffset = (categoryIndex, typeOffset) => {
    if (categoryIndex === 0) {
      indexOffset = typeOffset;
      return 0;
    }
    const categoryOffset = indexOffset;
    indexOffset += typeOffset;
    return categoryOffset;
  };
  const hasClearHistoryDisplayed = filteredSuggestions[0]?.key === RECENT_SEARCHES_KEY;
  updateHasRecentSearchDisplayed(hasClearHistoryDisplayed);
  // Increase all list item index by 1 as clear history is displayed
  const indexIncrement = hasClearHistoryDisplayed ? 1 : 0;

  const suggestionRef = useRef(null);
  const [expectedHeight, setExpectedHeight] = useState(-1);

  /* Functionality covered by playwright, run "npx playwright test --ui" to run the test */
  /* istanbul ignore next */
  useEffect(() => {
    const keypressEvent = (event) => {
      // Due to the delay on updating introduced to allow handlers to finish before updating
      // this timeout is needed to wait for the elements to be in the correct position.
      // TODO: Refactor input handling to avoid using multiple handlers with timeout
      setTimeout(() => {
        const targetSuggestionText = event.target.getAttribute('aria-activedescendant');
        if (!targetSuggestionText && suggestionRef.current) {
          suggestionRef.current.scrollTop = 0;
          return;
        }
        const targetSuggestionElement = suggestionRef.current?.querySelector(`button#${targetSuggestionText}`);
        if (!targetSuggestionElement) return;

        const suggestionListTop = suggestionRef.current.getBoundingClientRect().top;
        const selectedItemTop = targetSuggestionElement.getBoundingClientRect().top;
        const suggestionListBottom = suggestionRef.current.getBoundingClientRect().bottom;
        const selectedItemBottom = targetSuggestionElement.getBoundingClientRect().bottom;

        if (suggestionListBottom < selectedItemBottom) {
          suggestionRef.current.scrollTop += selectedItemBottom - suggestionListBottom;
        } else if (suggestionListTop > selectedItemTop) {
          suggestionRef.current.scrollTop -= suggestionListTop - selectedItemTop;
        }
      }, 0);
    };

    const viewportHeight = window.innerHeight;
    const suggestionPosition = suggestionRef.current.getBoundingClientRect().top;
    const heightToBottomOfViewport = viewportHeight - suggestionPosition - 32;
    if (heightToBottomOfViewport !== expectedHeight) {
      setExpectedHeight(heightToBottomOfViewport);
    }

    const inputField = inputWrapperRef?.current?.querySelector('input');
    if (inputField) inputField.addEventListener('keydown', keypressEvent);
    if (window.innerWidth < 800) document.documentElement.style.overflow = 'hidden';

    return () => {
      if (inputField) inputField.removeEventListener('keydown', keypressEvent);
      document.documentElement.style.overflow = null;
    };
  }, [suggestionRef, expectedHeight, setExpectedHeight, inputWrapperRef]);

  const mappedSuggestions = (
    <>{filteredSuggestions.map(({
      suggestions: typeSuggestions,
      key,
      copy,
      iconNode,
      iconProps,
    }, categoryIndex) => (
      key === RECENT_SEARCHES_KEY
        ? (
          <RecordRecentSearchesFeedback key={key} type={lob}>
            <OnVisible
              key={key}
              analyticsInfo={{
                element: getAnalyticsUtils({
                  type: key,
                  lob,
                  isRoundTrip,
                }),
                detail: 'recent-search-displayed',
                type: 'impression',
              }}
            >
              <CategoryList
                key={key}
                suggestions={typeSuggestions}
                headerCopy={copy}
                iconNode={iconNode}
                categoryKey={key}
                iconProps={iconProps}
                categoryIndex={categoryIndex}
                indexIncrement={indexIncrement}
                categoryOffset={getCategoryOffset(categoryIndex, typeSuggestions.length)}
              />
            </OnVisible>
          </RecordRecentSearchesFeedback>
        ) : (
          <CategoryList
            key={key}
            suggestions={typeSuggestions}
            headerCopy={copy}
            iconNode={iconNode}
            categoryKey={key}
            iconProps={iconProps}
            categoryIndex={categoryIndex}
            indexIncrement={indexIncrement}
            categoryOffset={getCategoryOffset(categoryIndex, typeSuggestions.length)}
          />
        )
    ))}
    </>
  );

  const redesignContent = (
    <div
      className={clsx(
        styles.variantSuggestionsWrapper,
        'position-absolute border color-surface-bg color-border-subtle margin-1-t shadow-3 radius-container margin-lr-sm-down'
      )}
    >
      <div
        className="scroll-y pad-1-tb"
        style={{ maxHeight: `${expectedHeight > 0 ? `${Math.floor(expectedHeight)}px` : 'unset'}` }}
        id="suggestionsListbox"
        role="listbox"
        ref={suggestionRef}
      >
        {mappedSuggestions}
      </div>
    </div>
  );

  return (
    <Experiment
      experimentName="prebooking_hp_search_widget_redesign"
      dimensionName="search_widget"
      defaultVariant="control"
    >
      <Variant variant="control">
        <div
          id="suggestionsListbox"
          className={clsx(
            styles.suggestionsWrapper,
            'position-absolute border color-surface-bg color-border-subtle margin-1-t shadow-3 pad-1',
            'radius-container-sm margin-1-lr-sm-down border-0-sm-down pad-2-b'
          )}
          role="listbox"
          ref={suggestionRef}
          style={{ maxHeight: `${expectedHeight > 0 ? `${Math.floor(expectedHeight)}px` : 'unset'}` }}
        >
          {mappedSuggestions}
        </div>
      </Variant>
      <Variant variant="widget_a_with_hero">
        {redesignContent}
      </Variant>
      <Variant variant="widget_a_no_hero">
        {redesignContent}
      </Variant>
      <Variant variant="widget_b_with_hero">
        {redesignContent}
      </Variant>
      <Variant variant="widget_b_no_hero">
        {redesignContent}
      </Variant>
    </Experiment>
  );
};

SuggestionsList.propTypes = {
  suggestions: PropTypes.arrayOf(PropTypes.shape(locationShape)),
};

SuggestionsList.defaultProps = {
  suggestions: [],
};

export { SuggestionsList as TestableSuggestions };
export default SuggestionsList;
